"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import Image from "next/image";
import { Text, useComponentProps } from "@sitecore-content-sdk/nextjs";
import type { EmblaOptionsType } from "embla-carousel";
import { useTranslations } from "next-intl";
import Button from "../../../atoms/Button";
import CustomEmblaCarousel from "../../../molecules/Embla/EmblaCarousel";
import { ICONS } from "../../../variables/icons";
import {
  ECOLAB_PLACEHOLDER,
  generateShimmerBlur,
} from "../../../utils/imageUtils";
import { type CardCarouselProps } from "./card-carousel.props";
import fallbackImage from "src/assets/images/fallback-image.jpg";
import { useArticleContext } from "contexts/ArticleContext";
import { useNewsContext } from "contexts/NewsContext";
import { useEventContext } from "contexts/EventContext";
import { useStoryContext } from "contexts/StoriesContext";
import { useLocationContext } from "contexts/LocationContext";
import { firstNonEmpty } from "src/utils";

// Client implementations of the CardCarousel variants. Imported by the
// server-safe entry `CardCarousel.tsx`, which exposes the public
// `Default` / `EventSlider` exports (thin server wrappers) plus
// `getComponentServerProps` (server data-fetch).

const CARD_IMAGE_WIDTH = 296;
const CARD_IMAGE_HEIGHT = 168;
const PULSE_UNDERLINE_DURATION_MS = 1000;
const MAX_DESCRIPTION_PREVIEW = 120;
const MAX_EVENT_SLIDER_CARDS = 12;
const DEFAULT_NUMBER_OF_CARDS = 8;

const SHARED_EMBLA_OPTIONS: EmblaOptionsType = {
  loop: true,
  active: true,
  slidesToScroll: 1,
  align: "start",
};

const isExternalImage = (imageUrl?: string): boolean =>
  imageUrl?.startsWith("http://") || imageUrl?.startsWith("https://") || false;

interface CardImageProps {
  src: string;
  alt: string;
  className: string;
}

// Picks `fill` for site-served images (so Next.js can size them via the
// container) and explicit width/height for external Coveo URLs that are not
// in the remotePatterns whitelist for optimisation.
const CardImage = ({ src, alt, className }: CardImageProps) => {
  if (!src) {
    return (
      <Image
        src={ECOLAB_PLACEHOLDER}
        alt={alt}
        width={CARD_IMAGE_WIDTH}
        height={CARD_IMAGE_HEIGHT}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
        quality={85}
        className={className}
        loading="lazy"
        priority={false}
      />
    );
  }

  const external = isExternalImage(src);
  const sharedProps = {
    src,
    alt,
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw",
    quality: 85,
    placeholder: "blur" as const,
    blurDataURL: generateShimmerBlur(CARD_IMAGE_WIDTH, CARD_IMAGE_HEIGHT),
    loading: "lazy" as const,
    priority: false,
    className,
  };

  return external ? (
    <Image
      {...sharedProps}
      width={CARD_IMAGE_WIDTH}
      height={CARD_IMAGE_HEIGHT}
    />
  ) : (
    <Image {...sharedProps} fill />
  );
};

// Closes hover/touch state when the user interacts outside the card list.
// Returned ref array is attached to each rendered card root.
const useCardOutsideInteraction = (clear: () => void) => {
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const handleInteractionOutside = (
      event: globalThis.MouseEvent | TouchEvent,
    ) => {
      const target = event.target as Node;
      const insideCard = cardRefs.current.some((ref) => ref?.contains(target));
      if (!insideCard) clear();
    };

    document.addEventListener("mousedown", handleInteractionOutside);
    document.addEventListener("touchstart", handleInteractionOutside);
    return () => {
      document.removeEventListener("mousedown", handleInteractionOutside);
      document.removeEventListener("touchstart", handleInteractionOutside);
    };
  }, [clear]);

  return cardRefs;
};

const PULSE_UNDERLINE_STYLE: CSSProperties = {
  textDecoration: "underline",
  textDecorationSkipInk: "none",
  WebkitTextDecorationSkip: "none",
};

const navigateTo = (link: string) => {
  globalThis.location.href = link;
};

const getTextColor = (styles?: string): string =>
  styles?.includes("text-white") ? "text-white" : "";

// ---------------------------------------------------------------------------
// Default variant
// ---------------------------------------------------------------------------

export const Default = (props: CardCarouselProps) => {
  const serverData = useComponentProps<{
    cards?: typeof props.cards;
    numberOfResultsRequested?: typeof props.numberOfResultsRequested;
  }>(props.rendering?.uid);

  const { article } = useArticleContext();
  const { news } = useNewsContext();
  const { event } = useEventContext();
  const { story } = useStoryContext();
  const { location } = useLocationContext();
  const pageId =
    firstNonEmpty(article?.id, news?.id, event?.id, story?.id, location?.id) ??
    undefined;

  const cards = useMemo(() => {
    const tempCards = props.cards ?? serverData?.cards ?? [];
    const filteredCards = tempCards.filter((card) => card.pageId !== pageId);
    if (
      filteredCards.length <=
      (serverData?.numberOfResultsRequested ?? DEFAULT_NUMBER_OF_CARDS)
    )
      return filteredCards;
    //Remove 1 extra requested result incase if the result list doesn't include current page
    if (tempCards.length == filteredCards.length)
      return filteredCards.slice(0, -1);
    return filteredCards;
  }, [
    props.cards,
    serverData?.cards,
    pageId,
    serverData?.numberOfResultsRequested,
  ]);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  const id = params?.RenderingIdentifier;
  const textColor = getTextColor(params?.Styles);
  const isArticleSlider = params?.Styles?.includes("article-slider");
  const titleColor =
    isArticleSlider && textColor === "" ? "text-dark-black" : textColor;

  const t = useTranslations();
  const showMoreFallback = t.has("show-more") ? t("show-more") : "Show More";
  const learnMorePrefix = t.has("learn-more-about")
    ? t("learn-more-about")
    : "Learn more about";

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState<number | null>(null);
  const [showUnderline, setShowUnderline] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const clearInteraction = useCallback(() => {
    setIsTouched(false);
    setActiveIndex(null);
    setCurrentCardIndex(null);
  }, []);
  const cardRefs = useCardOutsideInteraction(clearInteraction);

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent, link: string) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        navigateTo(link);
      }
    },
    [],
  );

  const handleCtaPulse = useCallback((event: ReactMouseEvent) => {
    event.stopPropagation();
    setShowUnderline(true);
    setTimeout(() => setShowUnderline(false), PULSE_UNDERLINE_DURATION_MS);
  }, []);

  const slides = useMemo(
    () =>
      cards.map((card, index) => {
        const linkLabel = card.linkLabel || showMoreFallback;
        const isActiveCard = isTouched && currentCardIndex === index;
        const titleActive = activeIndex === index;
        const truncatedDesc =
          card?.desc?.length > MAX_DESCRIPTION_PREVIEW
            ? `${card.desc.substring(0, MAX_DESCRIPTION_PREVIEW)}...`
            : card.desc;

        return (
          <div
            key={`${card.title}-${index}`}
            ref={(el) => {
              if (el) cardRefs.current[index] = el;
            }}
            className={`wrapper p-0 m-[10px_8px] bg-white transition-all duration-300 ease-in-out relative rounded-lg shadow-[0px_11px_20px_rgba(0,0,0,0.1)] border ${
              isActiveCard
                ? "border-teal shadow-[0_2px_8px_1px_rgba(0,0,0,0.6)]"
                : "border-lighter-gray hover:border-teal hover:shadow-[0px_18px_20px_-4px_rgba(0,0,0,0.08)]"
            }`}
            onClick={() => setCurrentCardIndex(index)}
            onTouchStart={() => {
              setIsTouched(true);
              setCurrentCardIndex(index);
            }}
          >
            <div
              className="cardWrapper relative cursor-pointer pb-[10px]"
              role="button"
              tabIndex={0}
              onClick={() => navigateTo(card.link)}
              onKeyDown={(e) => handleCardKeyDown(e, card.link)}
            >
              <div
                className={`imageBox relative h-px pb-[calc(0%+168px)] mb-0 rounded-[6px] ${
                  isTouched ? "border-teal" : ""
                }`}
              >
                <CardImage
                  src={card.image}
                  alt={card.title}
                  className="object-cover absolute top-0 left-0 h-[168px] w-full max-w-full block rounded-tl-[6px] rounded-tr-[6px]"
                />
              </div>

              <div className="textWrapper px-[20px] pt-[20px] pb-[17px] bg-white rounded-[6px] flex flex-col h-full">
                <div className="subTitleBox min-h-[24px] mb-[10px]">
                  {card.date ? (
                    <p className="font-normal text-base leading-[24px] tracking-[0] text-dark-gray">
                      {card.date}
                    </p>
                  ) : (
                    <span className="invisible">placeholder</span>
                  )}
                </div>

                <h3
                  className={`titleBox z-[2] text-[20px] font-normal md:text-2xl leading-[1.4] md:leading-[1.34] tracking-[-0.2px] h-[84px] md:h-[94px] mb-[10px] mt-0 mx-0 overflow-hidden hover:text-ecolab-blue ${
                    titleActive ? "text-ecolab-blue" : "text-black"
                  }`}
                  onClick={() => setActiveIndex(index)}
                  onTouchStart={() => setActiveIndex(index)}
                >
                  {card.title}
                </h3>

                <div className="descriptionBox min-h-[72px] mb-[12px]">
                  {card.desc ? (
                    <p
                      className="font-normal text-base leading-[24px] tracking-[0] text-text-gray overflow-hidden text-ellipsis line-clamp-3"
                      title={truncatedDesc}
                    >
                      {truncatedDesc}
                    </p>
                  ) : (
                    <span className="invisible">placeholder</span>
                  )}
                </div>

                <div
                  className="mt-auto -ml-[6px]"
                  onClick={handleCtaPulse}
                  role="presentation"
                >
                  <Button
                    field={{
                      value: {
                        href: card.link,
                        text: linkLabel,
                        title: card.title,
                      },
                    }}
                    buttonType="tertiary"
                    ariaLabel={`${learnMorePrefix} ${card.title}`}
                    jssIconField={{ value: ICONS.CHEVRON_RIGHT }}
                    iconPosition="right"
                    className={`customButton justify-end text-base font-bold leading-[28px] text-ecolab-blue no-underline cursor-pointer break-words !px-[6px] m-0 !min-h-0 !min-w-0 flex-row-reverse hover:underline focus:underline focus-visible:underline active:underline focus:font-bold active:font-bold visited:font-bold !font-bold focus:underline! active:underline! focus:border-0! active:border-0! ${
                      showUnderline ? "underline" : ""
                    }`}
                    style={showUnderline ? PULSE_UNDERLINE_STYLE : undefined}
                    data-role="link"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }),
    [
      cards,
      activeIndex,
      currentCardIndex,
      isTouched,
      showUnderline,
      learnMorePrefix,
      showMoreFallback,
      handleCardKeyDown,
      handleCtaPulse,
      cardRefs,
    ],
  );

  return (
    <div
      className="cardcarousel-component w-full max-w-[1408px] px-4 mx-auto mb-[var(--global-component-spacing)] pt-5"
      id={id ?? undefined}
      data-role="cardcarousel-component"
    >
      <div className="text-center">
        {(isPageEditing || fields?.Title?.value) && (
          <Text
            field={fields?.Title}
            tag="h2"
            className={`mt-5 mb-0 leading-[40.04px] lg:leading-[1.25] pb-5 z-[1] after:content-[''] after:block after:clear-both after:w-[50%] lg:after:w-[17%] after:mx-auto after:pt-[15px] after:leading-[1.25] after:text-[3.2rem] after:border-b-2 after:border-teal ${titleColor}`}
            data-testid="carousel-title"
          />
        )}
        {(isPageEditing || fields?.SubTitle?.value) && (
          <Text
            field={fields?.SubTitle}
            tag="p"
            className={`font-light text-base md:text-[21px] leading-[1.4] mb-5 ${textColor}`}
            data-testid="carousel-subtitle"
          />
        )}
      </div>

      <div className="pt-0 pb-0 lg:px-[60px] relative">
        {cards.length === 0 && (
          <div>
            <p className={`text-xl text-center font-medium my-8 ${textColor}`}>
              {fields?.ResultsNotFoundText?.value}
            </p>
          </div>
        )}
        <CustomEmblaCarousel
          slides={slides}
          options={SHARED_EMBLA_OPTIONS}
          classNames="!basis-[100%] lg:!basis-[25%] md:!basis-[33.33%]"
          autoPlayInterval={0}
          enableAutoplay={false}
          componentName="CardCarousel"
        />
      </div>

      {(isPageEditing || fields?.Link?.value) && (
        <div className="w-full text-center flex justify-center">
          <Button
            field={fields?.Link}
            size="default"
            buttonType={
              fields?.Link?.value?.class ||
              (textColor === "text-white" ? "primary-reverse" : "primary")
            }
            className="z-[1] mt-[52px] justify-self-center"
            iconName={fields?.Link?.value?.class ? "CHEVRON_BLUE_RIGHT" : ""}
            data-testid="carousel-cta"
            data-role="link"
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// EventSlider variant
// ---------------------------------------------------------------------------

export const EventSlider = (props: CardCarouselProps) => {
  const serverData = useComponentProps<{
    cards?: typeof props.cards;
    numberOfResultsRequested?: typeof props.numberOfResultsRequested;
  }>(props.rendering?.uid);

  const { article } = useArticleContext();
  const { news } = useNewsContext();
  const { event } = useEventContext();
  const { story } = useStoryContext();
  const { location } = useLocationContext();
  const pageId =
    firstNonEmpty(article?.id, news?.id, event?.id, story?.id, location?.id) ??
    undefined;

  const cards = useMemo(() => {
    const tempCards = props.cards ?? serverData?.cards ?? [];
    const filteredCards = tempCards.filter((card) => card.pageId !== pageId);
    if (
      filteredCards.length <=
      (serverData?.numberOfResultsRequested ?? DEFAULT_NUMBER_OF_CARDS)
    )
      return filteredCards;
    //Remove 1 extra requested result incase if the result list doesn't include current page
    if (tempCards.length == filteredCards.length)
      return filteredCards.slice(0, -1);
    return filteredCards;
  }, [
    props.cards,
    serverData?.cards,
    pageId,
    serverData?.numberOfResultsRequested,
  ]);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  const id = params?.RenderingIdentifier;
  const textColor = getTextColor(params?.Styles);
  const ctaParam = fields?.Link;
  const numberOfResults = fields?.NumberOfResults?.value || cards.length;
  const maxCards = Math.min(
    numberOfResults,
    cards.length,
    MAX_EVENT_SLIDER_CARDS,
  );

  const t = useTranslations();
  const showMoreFallback = t.has("show-more") ? t("show-more") : "Show More";
  const showMoreAboutPrefix = t.has("show-more-about")
    ? t("show-more-about")
    : "Show More about";

  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);
  const [isTapped, setIsTapped] = useState(false);

  const clearInteraction = useCallback(() => {
    setClickedIndex(null);
    setActiveCardIndex(null);
  }, []);
  const cardRefs = useCardOutsideInteraction(clearInteraction);

  const handleCardClick = useCallback((index: number, link: string) => {
    setActiveCardIndex(index);
    navigateTo(link);
  }, []);

  const handleCtaTap = useCallback(() => {
    setIsTapped(false);
    requestAnimationFrame(() => {
      setIsTapped(true);
      setTimeout(() => setIsTapped(false), PULSE_UNDERLINE_DURATION_MS);
    });
  }, []);

  const slides = useMemo(
    () =>
      cards.slice(0, maxCards).map((card, index) => {
        const linkLabel = card.linkLabel || showMoreFallback;
        const truncatedDesc =
          card?.desc?.length > MAX_DESCRIPTION_PREVIEW
            ? `${card.desc.substring(0, MAX_DESCRIPTION_PREVIEW)}...`
            : card.desc;

        return (
          <div
            key={`${card.title}-${index}`}
            ref={(el) => {
              if (el) cardRefs.current[index] = el;
            }}
            data-testid="event-slider-card"
            className={`wrapper p-0 m-[10px_8px] bg-white transition-[box-shadow,border-color] duration-300 ease-in-out relative rounded-[8px] shadow-[0px_4px_10px_rgba(0,0,0,0.1)] border ${
              activeCardIndex === index
                ? "border-teal shadow-[0_2px_8px_1px_rgba(0,0,0,0.6)]"
                : "border-lighter-gray hover:border-teal hover:shadow-[0_2px_8px_1px_rgba(0,0,0,0.6)]"
            }`}
          >
            <div
              className="cardWrapper relative cursor-pointer pb-[10px]"
              onClick={() => handleCardClick(index, card.link)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCardClick(index, card.link);
                }
              }}
            >
              {card.image && (
                <div className="imageBox relative h-[168px] mb-0 rounded-[6px]">
                  <CardImage
                    src={card.image}
                    alt={card.title}
                    className="object-cover absolute top-0 left-0 h-[168px] w-full max-w-full block rounded-tl-[6px] rounded-tr-[6px]"
                  />
                </div>
              )}

              <div className="textWrapper px-[20px] pt-[20px] pb-[17px] bg-white rounded-[6px]">
                <div className="min-h-[64px] mb-[10px]">
                  <h3
                    className={`text-[20px] leading-[28px] lg:text-[24px] lg:leading-[32px] font-normal tracking-[-0.2px] hover:text-ecolab-blue mb-[10px] line-clamp-2 ${
                      clickedIndex === index ? "text-ecolab-blue" : "text-black"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setClickedIndex(index);
                    }}
                  >
                    {card.title}
                  </h3>
                </div>

                <div className="h-[28px] mb-[8px] flex items-center">
                  {card.subtitle ? (
                    <h4 className="text-[14px] leading-[21px] tracking-[0.4px] lg:text-[16px] lg:leading-[24px] font-medium text-ecolab-blue truncate">
                      {card.subtitle}
                    </h4>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>

                <div className="min-h-[42px]">
                  {card.date && (
                    <p className="font-bold text-[14px] leading-[20px] text-charcoal-blue mb-[6px] cursor-default">
                      {card.date}
                    </p>
                  )}
                </div>

                <div className="min-h-[40px] h-[38px] overflow-hidden">
                  {card.timezone && (
                    <p className="text-[13.6px] font-bold leading-[19px] text-charcoal-blue mb-2 cursor-default">
                      {card.timezone}
                    </p>
                  )}
                </div>

                <div className="min-h-[72px] mb-[12px]">
                  {card.desc && (
                    <p
                      className="text-base text-text-gray leading-[24px] line-clamp-3 overflow-hidden"
                      title={truncatedDesc}
                    >
                      {truncatedDesc}
                    </p>
                  )}
                </div>

                <div
                  className="mt-auto -ml-[6px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCtaTap();
                  }}
                  role="presentation"
                >
                  <Button
                    field={{
                      value: {
                        href: card.link,
                        text: linkLabel,
                        title: card.title,
                      },
                    }}
                    buttonType="tertiary"
                    ariaLabel={`${showMoreAboutPrefix} ${card.title}`}
                    jssIconField={{ value: ICONS.CHEVRON_RIGHT }}
                    iconPosition="right"
                    tabIndex={0}
                    style={
                      isTapped
                        ? {
                            ...PULSE_UNDERLINE_STYLE,
                            WebkitTapHighlightColor: "transparent",
                          }
                        : undefined
                    }
                    className={`text-base justify-end font-bold text-ecolab-blue leading-[22.8571px] hover:underline focus:underline focus-visible:underline active:underline focus:font-bold active:font-bold visited:font-bold !font-bold ios-active-underline focus:underline! active:underline! focus:border-0! active:border-0! ${
                      isTapped ? "underline" : ""
                    }`}
                    data-role="link"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }),
    [
      cards,
      maxCards,
      activeCardIndex,
      clickedIndex,
      isTapped,
      showMoreAboutPrefix,
      showMoreFallback,
      handleCardClick,
      handleCtaTap,
      cardRefs,
    ],
  );

  return (
    <div
      className="cardcarousel-component bg-transparent w-full max-w-[1408px] px-4 mx-auto mb-[var(--global-component-spacing)] pt-5"
      id={id ?? undefined}
      data-role="cardcarousel-component"
    >
      <div className="text-center">
        {(isPageEditing || fields?.Title?.value) && (
          <Text
            field={fields?.Title}
            tag="h2"
            className={`mt-2 mb-10 tracking-[0.8px] leading-[40px] ${textColor}`}
            data-testid="eventslider-title"
          />
        )}
      </div>

      <div className="pt-0 pb-0 lg:px-[60px] relative">
        {cards.length === 0 && (
          <div>
            <p className={`text-xl text-center font-medium my-8 ${textColor}`}>
              {fields?.ResultsNotFoundText?.value}
            </p>
          </div>
        )}
        <CustomEmblaCarousel
          slides={slides}
          options={SHARED_EMBLA_OPTIONS}
          classNames="!basis-[100%] lg:!basis-[25%] md:!basis-[33.33%]"
          autoPlayInterval={0}
          enableAutoplay={false}
          componentName="EventSlider"
        />
      </div>

      <div className="flex justify-center text-center mt-10 mb-[var(--global-component-spacing)]">
        <Button
          buttonType={
            ctaParam?.value?.class ||
            (textColor === "text-white" ? "primary-reverse" : "primary")
          }
          field={ctaParam}
          iconName={ctaParam?.value?.class ? "CHEVRON_BLUE_RIGHT" : ""}
          tabIndex={0}
          ariaLabel={ctaParam?.value?.text}
          className="justify-self-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
          data-testid="eventslider-cta"
          data-role="link"
        />
      </div>
    </div>
  );
};

export const MediaCenterBillboard = (props: CardCarouselProps) => {
  const id = props?.params?.RenderingIdentifier;
  const { page, fields } = props;
  const isPageEditing = page?.mode?.isEditing;

  const serverData = useComponentProps<{
    cards?: typeof props.cards;
    numberOfResultsRequested?: typeof props.numberOfResultsRequested;
  }>(props.rendering?.uid);

  const card = props?.cards?.[0] ?? serverData?.cards?.[0] ?? null;

  if (!card && !isPageEditing) {
    return null;
  }
  const handleCardClick = (link?: string) => {
    if (link) {
      globalThis.location.href = link;
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent, link?: string) => {
    if ((event.key === "Enter" || event.key === " ") && link) {
      event.preventDefault();
      globalThis.location.href = link;
    }
  };
  const imageProps = card?.image
    ? {
        src: card.image,
        alt: card.title,
        width: isExternalImage(card.image) ? 550 : undefined,
        height: isExternalImage(card.image) ? 310 : undefined,
        fill: !isExternalImage(card.image),
        sizes: "(max-width: 768px) 100vw, 50vw",
        quality: 85,
        priority: true,
        placeholder: "blur" as const,
        blurDataURL: generateShimmerBlur(550, 310),
      }
    : null;

  const truncatedDesc =
    card?.desc?.length > MAX_DESCRIPTION_PREVIEW
      ? `${card.desc.substring(0, MAX_DESCRIPTION_PREVIEW)}...`
      : card?.desc;
  return (
    <div
      className="cardcarousel-component w-full max-w-[1288px] px-4 mx-auto mb-[var(--global-component-spacing)] pt-5"
      id={id ?? undefined}
      data-role="media-center-billboard"
    >
      {!card && isPageEditing && (
        <div>
          <p className="text-xl text-center font-medium my-8">
            {fields?.ResultsNotFoundText?.value}
          </p>
        </div>
      )}
      {card && (
        <div className="pt-0 pb-0 relative">
          <div
            data-testid="media-center-billboard-card"
            className="wrapper relative p-0 m-0 overflow-hidden rounded-[6px] bg-white shadow-[0px_11px_20px_rgba(0,0,0,0.1)]"
          >
            <div
              className="cardWrapper relative cursor-pointer flex flex-col md:flex-row min-h-[310px] md:min-h-[465px]"
              onClick={() => handleCardClick(card.link)}
              onKeyDown={(event) => handleKeyDown(event, card.link)}
              role="link"
              tabIndex={0}
            >
              <div className="imageBox relative w-full h-[310px] md:order-2 md:w-[calc(50%+56px)] md:h-auto md:-ml-[56px] overflow-hidden z-[1]">
                {card.image && imageProps ? (
                  <Image
                    {...imageProps}
                    alt={card.title}
                    className="object-cover object-left absolute top-0 left-0 h-full w-full max-w-full block"
                    loading="eager"
                  />
                ) : (
                  <Image
                    src={fallbackImage}
                    alt={card.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    quality={85}
                    priority
                    className="object-cover object-left absolute top-0 left-0 h-full w-full max-w-full block"
                    loading="eager"
                  />
                )}
              </div>
              <div
                className={`textWrapper hero-bg-pattern relative z-[2] md:order-1 md:w-1/2 min-h-[360px] md:min-h-[465px] px-[20px] py-[28px] md:px-[40px] md:py-[26px] text-white flex flex-col`}
              >
                <div className="relative z-[3] flex flex-col h-full">
                  <div className="subTitleBox mb-[20px]">
                    {card.date ? (
                      <p className="text-[16px] leading-[24px] md:text-[16px] md:leading-[24px] tracking-[0] text-white m-0">
                        {card.date}
                      </p>
                    ) : (
                      <span className="invisible">placeholder</span>
                    )}
                  </div>
                  <h3 className="!mt-0 mx-0 mb-[20px] !font-bold titleBox z-[2] text-[32px] leading-[1.2] font-normal tracking-[-0.2px] text-white max-w-[720px]">
                    {card.title}
                  </h3>
                  <div className="descriptionBox mb-[32px] max-w-[720px]">
                    {card.desc ? (
                      <p
                        className="text-[16px] leading-[24px] md:text-[16px] md:leading-[24px] tracking-[0] text-white overflow-hidden text-ellipsis line-clamp-2 m-0"
                        title={card.desc}
                      >
                        {truncatedDesc}
                      </p>
                    ) : (
                      <span className="invisible">placeholder</span>
                    )}
                  </div>
                  <div className="mt-0">
                    <Button
                      field={{
                        value: {
                          href: card.link,
                          text: card.linkLabel,
                          title: card.title,
                        },
                      }}
                      buttonType="primary-reverse"
                      ariaLabel={`Learn more about ${card.title}`}
                      jssIconField={{ value: ICONS.CHEVRON_RIGHT }}
                      iconPosition="right"
                      className="customButton
                        !inline-flex !w-fit !max-w-fit !self-start
                        !bg-white !text-ecolab-blue !rounded-full !px-[24px] !py-[12px] !h-[52px]
                        text-[16px] leading-[28px] font-bold cursor-pointer no-underline
                        flex-row-reverse !justify-center !items-center
                        hover:!bg-white hover:!text-dark-blue hover:no-underline
                        focus:!bg-white focus:!text-ecolab-blue focus:no-underline
                        active:!bg-white active:!text-ecolab-blue active:no-underline
                        "
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                      }}
                      data-role="link"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
